import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getVolatilityMetrics, VolatilityMetrics } from '../api/volatility';
import { formatDollar } from '../utils/currencyFormatter';
import './VolatilityInfoPanel.css';

interface VolatilityInfoPanelProps {
  productId: string;
  totalInvestment: string;
  numLadders: number;
  tradesPerLadder: number;
  initialDeploymentPct: string;
  lowVolatilityPositionMultiplier: string;
  highVolatilityPositionMultiplier: string;
  volatilityPeriod: string;
  granularity?: number;
  lookback?: number;
}

/**
 * VolatilityInfoPanel - Displays current volatility information for trade preview
 *
 * Shows:
 * - Current volatility regime
 * - Position size multiplier effect
 * - Initial vs full deployment amounts
 * - Adjusted ladder spacing
 */
const VolatilityInfoPanel: React.FC<VolatilityInfoPanelProps> = ({
  productId,
  totalInvestment,
  numLadders,
  tradesPerLadder,
  initialDeploymentPct,
  lowVolatilityPositionMultiplier,
  highVolatilityPositionMultiplier,
  volatilityPeriod,
  granularity = 60,
  lookback = 7,
}) => {
  // Fetch current volatility metrics
  const {
    data: volatilityMetrics,
    isLoading,
    isError,
    error,
  } = useQuery<VolatilityMetrics>({
    queryKey: ['volatilityMetrics', productId, granularity, lookback],
    queryFn: () => getVolatilityMetrics(productId, granularity, lookback),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1, // Only retry once
  });

  // Handle errors in useEffect instead of deprecated onError callback
  React.useEffect(() => {
    if (isError && error) {
      console.error('Failed to load volatility metrics:', error);
      console.error('Request parameters:', { productId, granularity, lookback });
      console.info('Possible causes:');
      console.info('1. ATR metrics not calculated for this product/granularity');
      console.info('2. API endpoint not accessible');
      console.info('3. Authentication issue');
      console.info('Trade group creation will proceed without volatility preview');
    }
  }, [isError, error, productId, granularity, lookback]);

  /**
   * Get regime color for visual indication
   */
  const getRegimeColor = (regime: string): string => {
    switch (regime) {
      case 'LOW':
        return '#10b981'; // green
      case 'NORMAL':
        return '#3b82f6'; // blue
      case 'HIGH':
        return '#f97316'; // orange
      case 'EXTREME':
        return '#ef4444'; // red
      default:
        return '#6b7280'; // gray
    }
  };

  /**
   * Calculate effective position multiplier based on current regime
   */
  const getEffectiveMultiplier = (): number => {
    if (!volatilityMetrics) return 1.0;
    
    switch (volatilityMetrics.volatility_regime) {
      case 'LOW':
        return parseFloat(lowVolatilityPositionMultiplier);
      case 'HIGH':
        return parseFloat(highVolatilityPositionMultiplier);
      case 'EXTREME':
        return parseFloat(highVolatilityPositionMultiplier) * 0.5; // Half of HIGH multiplier
      default:
        return 1.0;
    }
  };

  /**
   * Format currency
   */
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="volatility-info-panel loading">
        <div className="loading-message">Loading volatility data...</div>
      </div>
    );
  }

  if (isError || !volatilityMetrics) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const isNotFound = errorMessage.includes('404') || errorMessage.includes('Not Found');

    return (
      <div className="volatility-info-panel error">
        <div className="error-message">
          <strong>ℹ️ Volatility Preview Unavailable</strong>
          <p style={{ marginTop: '0.5rem', fontSize: '13px', lineHeight: '1.5' }}>
            {isNotFound ? (
              <>
                No volatility data found for {productId} with {granularity}-minute granularity.
                ATR metrics may not be calculated yet for this product.
              </>
            ) : (
              <>
                Current market volatility metrics cannot be loaded at this time.
                Error: {errorMessage}
              </>
            )}
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
            This is informational only and does not affect trade group creation.
            Your trade group will be created with the configured parameters, and
            volatility-based position sizing will activate once the trade group is live.
          </p>
        </div>
      </div>
    );
  }

  const totalInvestmentNum = parseFloat(totalInvestment);
  const initialDeploymentNum = parseFloat(initialDeploymentPct) / 100;
  const initialDeploymentAmount = totalInvestmentNum * initialDeploymentNum;
  const fullDeploymentAmount = totalInvestmentNum;
  const effectiveMultiplier = getEffectiveMultiplier();
  const totalTrades = numLadders * tradesPerLadder;
  const basePositionSize = initialDeploymentAmount / totalTrades;
  const adjustedPositionSize = basePositionSize * effectiveMultiplier;

  return (
    <div className="volatility-info-panel">
      <h3 className="panel-title">
        <span className="volatility-icon">📊</span>
        Volatility-Adjusted Position Sizing
      </h3>

      <div className="volatility-metrics">
        <div className="metric-row">
          <span className="metric-label">Current Regime:</span>
          <span
            className="regime-badge"
            style={{ backgroundColor: getRegimeColor(volatilityMetrics.volatility_regime) }}
          >
            {volatilityMetrics.volatility_regime}
          </span>
        </div>

        <div className="metric-row">
          <span className="metric-label">Volatility Percentile:</span>
          <span className="metric-value">{volatilityMetrics.volatility_percentile.toFixed(1)}%</span>
        </div>

        <div className="metric-row">
          <span className="metric-label">Current ATR:</span>
          <span className="metric-value">{formatDollar(volatilityMetrics.current_atr, productId)}</span>
        </div>

        <div className="metric-row">
          <span className="metric-label">Position Multiplier:</span>
          <span className={`metric-value multiplier ${effectiveMultiplier < 1 ? 'reduced' : effectiveMultiplier > 1 ? 'increased' : ''}`}>
            {effectiveMultiplier.toFixed(2)}x
          </span>
        </div>
      </div>

      <div className="deployment-info">
        <h4>Capital Deployment Strategy</h4>
        
        <div className="deployment-row highlight">
          <span className="deployment-label">Initial Deployment ({(initialDeploymentNum * 100).toFixed(0)}%):</span>
          <span className="deployment-value">{formatCurrency(initialDeploymentAmount)}</span>
        </div>

        <div className="deployment-row">
          <span className="deployment-label">Full Deployment (100%):</span>
          <span className="deployment-value">{formatCurrency(fullDeploymentAmount)}</span>
        </div>

        <div className="deployment-row">
          <span className="deployment-label">Analysis Period:</span>
          <span className="deployment-value">{volatilityPeriod.replace('_', '-')} ({lookback} days)</span>
        </div>
      </div>

      <div className="position-sizing-info">
        <h4>Position Sizing Impact</h4>

        <div className="sizing-comparison">
          <div className="sizing-row">
            <span className="sizing-label">Base Position Size:</span>
            <span className="sizing-value">{formatCurrency(basePositionSize)}</span>
          </div>

          <div className="sizing-row adjusted">
            <span className="sizing-label">Adjusted Position Size:</span>
            <span className="sizing-value">{formatCurrency(adjustedPositionSize)}</span>
          </div>

          {effectiveMultiplier !== 1.0 && (
            <div className="sizing-explanation">
              {effectiveMultiplier < 1.0 ? (
                <span className="explanation-text reduced">
                  ⚠️ Positions reduced by {((1 - effectiveMultiplier) * 100).toFixed(0)}% due to {volatilityMetrics.volatility_regime} volatility
                </span>
              ) : (
                <span className="explanation-text increased">
                  ✓ Positions increased by {((effectiveMultiplier - 1) * 100).toFixed(0)}% due to {volatilityMetrics.volatility_regime} volatility
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="volatility-notes">
        <p className="note-text">
          <strong>Note:</strong> Position sizes will be automatically adjusted if the volatility regime changes. 
          The system monitors volatility every {volatilityPeriod === 'MEDIUM' ? '24 hours' : 'periodically'} and 
          rebalances when significant changes occur.
        </p>
      </div>
    </div>
  );
};

export default VolatilityInfoPanel;
